import styled from 'styled-components';

type ImageDimension = { width?: number; height?: number; quality?: number; rxy?: string };
export class PictureType {
    scaling: boolean = true;
    dimensionsDefault: ImageDimension = {};
    dimensions: ImageDimension[] = [];

    constructor(
        dimensions: ImageDimension[],
        dimensionsDefault: ImageDimension = { width: 1696, quality: 80, rxy: '0.5,0.5' },
        scaling: boolean = true,
    ) {
        this.dimensionsDefault = { ...this.dimensionsDefault, ...dimensionsDefault };
        this.dimensions = dimensions;
        this.scaling = scaling;
    }
}

class PictureData {
    srcSet: string[];
    srcSetLowQuality: string[];

    srcSetWebp: string[];
    srcSetWebpLowQuality: string[];

    imgSrc: string;
    imgSrcLowQuality: string;

    altText: string;

    constructor() {
        this.srcSet = [];
        this.srcSetLowQuality = [];
        this.srcSetWebp = [];
        this.srcSetWebpLowQuality = [];

        this.imgSrc = '';
        this.imgSrcLowQuality = '';
        this.altText = '';
    }
}

export type PictureProps = {
    imageUrl: string;
    altText?: string;
    picture?: PictureType;
    scaling?: boolean;
    className?: string;
};

/* 
  The ImageSharp Configuration has
  4 breakpoints based on with otherwise it will 
  return the original image:
    - 639 px
    - 767 px
    - 1024 px
    - 1696 px

  To utilize focal point the height is required in type ImageDimension
  
*/
const Picture = ({
    imageUrl,
    altText,
    className,
    picture = new PictureType([
        { width: 639, quality: 80 },
        { width: 767, quality: 80 },
        { width: 1024, quality: 80 },
        { width: 1696, quality: 80 },
    ]),
    scaling = true,
}: PictureProps) => {
    const data = PictureUtils.GetPictureData(imageUrl, picture, false, scaling);
    const image = [...(data?.srcSet ?? [])].pop() ?? imageUrl;
    return (
        <StyledPicture>
            {scaling &&
                data &&
                data.srcSet.map((source, index) => (
                    <source key={index} media={`(max-width: ${picture.dimensions[index].width}px)`} srcSet={source} />
                ))}
            <img src={image} alt={altText} className={className} />
        </StyledPicture>
    );
};

const StyledPicture = styled.picture`
    all: inherit;
`;

export default Picture;

class PictureUtils {
    public static GetPictureData(
        imageUrl: string,
        picture: PictureType,
        includeLowQuality: boolean = false,
        scaling?: boolean,
    ): PictureData {
        const data = new PictureData();
        const currentFormat = PictureUtils.GetFormatFromExtension(imageUrl);

        if (picture.dimensions !== null) {
            data.srcSet = PictureUtils.BuildSrcSet(imageUrl, picture, currentFormat, false, scaling);
            data.imgSrc = PictureUtils.BuildQueryString(
                imageUrl,
                picture,
                picture.dimensionsDefault,
                currentFormat,
                0,
                scaling,
            );

            // if jpg, also add webp versions
            if (currentFormat === 'jpg') {
                data.srcSetWebp = PictureUtils.BuildSrcSet(imageUrl, picture, 'webp');
                if (includeLowQuality) {
                    data.srcSetWebpLowQuality = PictureUtils.BuildSrcSet(imageUrl, picture, 'webp', true);
                }
            }
        }

        return data;
    }

    private static BuildSizeAttribute(picture: PictureType): string {
        // Note: Pixel density seems to change the selected image. For example if 480px image is set to display
        // the pixel density of 2 is multiplied with the width which will force the bigger image to be served
        return picture.dimensions
            .map((width) => {
                return `(max-width: ${width}px) ${width}px`;
            })
            .join(', ');
    }

    private static BuildSrcSet(
        imageUrl: string,
        picture: PictureType,
        format: string,
        lowQuality: boolean = false,
        scaling: boolean = true,
    ): string[] {
        const lowQualityValue = format === 'webp' ? 1 : picture.dimensionsDefault.quality!; //webp can have lower quality value
        const lowQualityFormat = format === 'png' ? 'png8' : format; //low quality png will be 8-bit
        const srcset: string[] = [];

        for (const dimension of picture.dimensions) {
            if (lowQuality) {
                srcset.push(
                    PictureUtils.BuildQueryString(
                        imageUrl,
                        picture,
                        dimension,
                        lowQualityFormat,
                        lowQualityValue,
                        scaling,
                    ),
                );
            } else {
                srcset.push(PictureUtils.BuildQueryString(imageUrl, picture, dimension, format, 0, scaling));
            }
        }

        return srcset;
    }

    private static GetFormatFromExtension(url: string): string {
        let format = url?.split(/[#?]/)[0]?.split('.')?.pop()?.trim();
        if (format === 'jpeg') {
            format = 'jpg';
        }
        return format ?? '';
    }

    private static BuildQueryString(
        imageUrl: string,
        picture: PictureType,
        dimensions: ImageDimension,
        format: string,
        overrideQuality: number,
        scaling?: boolean,
    ): string {
        let url = null;
        if (!imageUrl) return '';

        if (imageUrl.startsWith('/')) {
            url = new URL(imageUrl, window.location.protocol + window.location.hostname);
        } else {
            url = new URL(imageUrl);
        }
        const params = url.searchParams;

        if (format === 'webp') {
            params.append('format', format);
        }

        if (format === 'png' || format === 'png8') {
            params.append('format', 'webp');
        }

        if (overrideQuality > 0) {
            params.append('quality', overrideQuality.toString());
        } else if (!params.get('quality') && dimensions?.quality) {
            params.append('quality', dimensions?.quality?.toString());
        } else if (picture.dimensionsDefault?.quality) {
            params.append('quality', picture.dimensionsDefault?.quality.toString());
        }

        if (scaling && dimensions.width) {
            params.append('width', dimensions.width.toString());

            if (dimensions.height) {
                params.append('height', dimensions.height.toString());
            }
        }

        if (scaling && dimensions.height && !params.get('rxy') && picture.dimensionsDefault.rxy) {
            params.append('rxy', picture.dimensionsDefault.rxy?.toString());
        }

        return url.toString();
    }
}
